import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import en from "../../language/Eng_Aust.json";
import { openUberEats } from "./uberEatsLink";

const slideData = [
  {
    id: 1,
    image: process.env.PUBLIC_URL + "/assets/Buttered/Buttered_store_image/Store_image2.webp",
    ...en.home.hero,
  },
  {
    id: 2,
    image: process.env.PUBLIC_URL + "/assets/Buttered/Buttered_store_image/Store_image7.webp",
    ...en.home.hero,
  },
  {
    id: 3,
    image: process.env.PUBLIC_URL + "/assets/Buttered/Buttered_store_image/Store_image8.webp",
    ...en.home.hero,
  },
];

const Photoline: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState<{ [key: number]: boolean }>({});

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % slideData.length);
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  const handleImageLoad = (id: number) => {
    setLoadedImages((prev) => ({ ...prev, [id]: true }));
  };

  const currentSlide = slideData[currentIndex];

  const handleMenuClick = () => {
    openUberEats();
  };

  const handleAboutClick = () => {
    window.location.href = "/about";
  };

  return (
    <Container>
      <ImageWrapper>
        <Slider index={currentIndex} total={slideData.length}>
          {slideData.map((slide, idx) => (
            <SlideWrapper key={slide.id}>
              {!loadedImages[slide.id] && <Skeleton />}
              <StyledImage
                src={slide.image}
                alt={slide.title}
                onLoad={() => handleImageLoad(slide.id)}
                $isLoaded={loadedImages[slide.id] || false}
                {...({ fetchpriority: idx === 0 ? "high" : "auto" } as any)}
                loading={idx === 0 ? "eager" : "lazy"}
              />
            </SlideWrapper>
          ))}
        </Slider>
        <OverlayContainer>
          <ContentWrapper>
            <Badge>{currentSlide.badge}</Badge>
            <MainTitle>{currentSlide.title}</MainTitle>
            <Description>{currentSlide.description}</Description>
            <ButtonGroup>
              <PrimaryButton onClick={handleMenuClick}>{currentSlide.primaryButton}</PrimaryButton>
              <SecondaryButton onClick={handleAboutClick}>{currentSlide.secondaryButton}</SecondaryButton>
            </ButtonGroup>
          </ContentWrapper>
        </OverlayContainer>
        <DotsWrapper>
          {slideData.map((_, idx) => (
            <Dot key={idx} $active={currentIndex === idx} onClick={() => setCurrentIndex(idx)} aria-label={`${en.home.hero.slideAriaLabel} ${idx + 1}`} aria-pressed={currentIndex === idx} />
          ))}
        </DotsWrapper>
      </ImageWrapper>
    </Container>
  );
};

export default Photoline;

const Container = styled.div`
  margin-top: 8px;
  width: 100%;
  display: flex;
  justify-content: center;
`;

const ImageWrapper = styled.div`
  width: 90%;
  max-width: 1200px;
  aspect-ratio: 16 / 9;
  max-height: 800px;
  min-height: 400px;
  border-radius: 36px;
  box-sizing: border-box;
  position: relative;
  overflow: hidden;
  background-color: #000;

  @media (max-width: 768px) {
    width: calc(100% - 32px);
    aspect-ratio: auto;
    height: 500px;
    border-radius: 30px;
  }
`;

const Slider = styled.div<{ index: number; total: number }>`
  display: flex;
  width: 100%;
  height: 100%;
  transform: translateX(-${({ index }) => index * 100}%);
  transition: transform 0.5s ease-in-out;
`;

const SlideWrapper = styled.div`
  width: 100%;
  height: 100%;
  flex-shrink: 0;
  position: relative;
`;

const skeletonPulse = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

const Skeleton = styled.div`
  position: absolute;
  top: 0; left: 0; width: 100%; height: 100%;
  background-color: #1a1a1a;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.05), transparent);
    animation: ${skeletonPulse} 1.5s infinite linear;
  }
`;

const StyledImage = styled.img<{ $isLoaded?: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
  filter: brightness(0.85);
  opacity: ${({ $isLoaded }) => ($isLoaded ? 1 : 0)};
  transition: opacity 0.5s ease-in-out;
`;

const OverlayContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: flex-start;
  z-index: 10;
  pointer-events: none;
  background: linear-gradient(180deg, transparent 10%, rgba(32, 25, 18, .35) 38%, rgba(32, 25, 18, .82) 100%);
`;

const ContentWrapper = styled.div`
  color: #ffffff;
  padding: 0 5%;
  padding-bottom: 60px;
  max-width: 700px;
  text-align: left;
  pointer-events: auto;

  @media (max-width: 768px) {
    max-width: 100%;
    box-sizing: border-box;
    padding: 0 20px;
    padding-bottom: 50px;
  }
`;

const Badge = styled.span`
  display: inline-block;
  background-color: #ffe66d;
  color: #333;
  font-size: 0.8rem;
  font-weight: bold;
  padding: 6px 12px;
  border-radius: 999px;
  margin-bottom: 16px;
  text-transform: uppercase;
  letter-spacing: 1px;

  @media (max-width: 768px) {
    font-size: 0.7rem;
    padding: 4px 10px;
    margin-bottom: 12px;
  }
`;

const MainTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 800;
  line-height: 1.2;
  margin: 0 0 16px 0;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);

  @media (max-width: 1024px) {
    font-size: 2.8rem;
  }
  @media (max-width: 768px) {
    font-size: 2rem;
  }
  @media (max-width: 480px) {
    font-size: clamp(1.75rem, 7.4vw, 2.2rem);
    letter-spacing: -1px;
  }
`;

const Description = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 32px;
  opacity: 0.9;
  max-width: 500px;

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 24px;
  }
  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
`;

const ButtonBase = styled.button`
  padding: 12px 28px;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 30px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  min-height: 48px;
  @media (max-width: 768px) { flex: 1; }

  @media (max-width: 768px) {
    padding: 10px 20px;
    font-size: 0.9rem;
  }
`;

const PrimaryButton = styled(ButtonBase)`
  background-color: #ffe66d;
  color: #333;

  &:hover {
    background-color: #e0d29a;
    transform: translateY(-2px);
  }
`;

const SecondaryButton = styled(ButtonBase)`
  background-color: transparent;
  color: #fff;
  border: 2px solid #fff;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    transform: translateY(-2px);
  }
`;

const DotsWrapper = styled.div`
  display: flex;
  justify-content: center;
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 20;
`;

const Dot = styled.button<{ $active: boolean }>`
  border: 0;
  padding: 0;
  position: relative;
  &::after { content: ""; position: absolute; inset: -17px -5px; }
  width: ${({ $active }) => ($active ? "30px" : "10px")};
  height: 10px;
  border-radius: 5px;
  background-color: ${({ $active }) => ($active ? "#ffe66d" : "#fff")};
  margin: 0 5px;
  opacity: ${({ $active }) => ($active ? 1 : 0.5)};
  transition: all 0.3s ease;
  cursor: pointer;
`;
